/**
 * ColorSystemShowcase — single pane showing every color token currently
 * registered in the Orka Design System, with a raw-code expander per
 * source file so other agents can copy-paste the canonical definitions.
 *
 * Sources covered (all verbatim):
 *   /app/frontend/src/design-system/tokens.css        (surface, text, border, elevation, semantic bg, light + dark)
 *   /app/frontend/src/design-system/priority.js       (PRIORITY_META)
 *   /app/frontend/src/design-system/states.js         (TASK_STATE_META)
 *   /app/frontend/src/design-system/intents.js        (INTENT_META)
 *   /app/frontend/src/design-system/agentTones.js     (AGENT_TONES)
 *   /app/frontend/src/design-system/effortScale.js    (DEFAULT_EFFORT_SCALE)
 *
 * Intentionally NOT included — user-defined per-org (not design-system tokens):
 *   - Work area / project colors (PROJECT_META in projects.js is a demo fixture only)
 */
import React, { useState } from 'react';
import { Check, Copy } from 'lucide-react';
import { OrkaText, OrkaSurface } from '../orka';
import { PRIORITY_META } from '../../design-system/priority';
import { TASK_STATE_META } from '../../design-system/states';
import { INTENT_META } from '../../design-system/intents';
import { AGENT_TONES } from '../../design-system/agentTones';
import { DEFAULT_EFFORT_SCALE } from '../../design-system/effortScale';

/* ———————————————————————————— RAW CODE SNIPPETS ——————————————————————————
 * Kept in-file (CRA can't use vite ?raw imports). Edit here if source changes.
 * These are the verbatim color blocks from each source file. Agents copy-pasting
 * any of these should get a complete, drop-in definition.
 * ————————————————————————————————————————————————————————————————————— */

const RAW_TOKENS_CSS = `/* /app/frontend/src/design-system/tokens.css — LIGHT MODE */
:root {
  /* === SURFACE (§6.1) === */
  --orka-surface-page: #fafaf9;          /* stone-50 */
  --orka-surface-base: #ffffff;
  --orka-surface-raised: #ffffff;
  --orka-surface-subtle: #f5f5f4;        /* stone-100 */
  --orka-surface-overlay: rgba(0,0,0,0.4);
  --orka-surface-glass: rgba(255,255,255,0.65);
  --orka-surface-selected: rgba(124,58,237,0.08);  /* violet@8% */
  --orka-surface-dragging: rgba(124,58,237,0.12);
  --orka-surface-drop-target: rgba(34,197,94,0.10);
  --orka-surface-disabled: #e7e5e4;       /* stone-200 */

  /* === TEXT (§6.2) === */
  --orka-text-primary: #0f172a;          /* slate-900 */
  --orka-text-secondary: #475569;        /* slate-600 */
  --orka-text-tertiary: #94a3b8;         /* slate-400 */
  --orka-text-disabled: #cbd5e1;         /* slate-300 */
  --orka-text-inverse: #ffffff;
  --orka-text-success: #059669;          /* emerald-600 */
  --orka-text-warning: #d97706;          /* amber-600 */
  --orka-text-danger: #dc2626;           /* red-600 */
  --orka-text-info: #0284c7;             /* sky-600 */
  --orka-text-intent: #7c3aed;           /* violet-600 */

  /* === BORDER === */
  --orka-border-subtle: #e7e5e4;          /* stone-200 */
  --orka-border-default: #d6d3d1;         /* stone-300 */
  --orka-border-strong: #78716c;          /* stone-500 */
  --orka-border-focus: #7c3aed;           /* violet-600 */

  /* === ELEVATION (shadows) === */
  --orka-shadow-sm: 0 1px 2px rgba(15,23,42,0.06);
  --orka-shadow-md: 0 4px 12px rgba(15,23,42,0.08);
  --orka-shadow-lg: 0 12px 32px rgba(15,23,42,0.12);
  --orka-shadow-glow-focus: 0 0 0 3px rgba(124,58,237,0.20);

  /* Semantic intent backgrounds (solid + soft). */
  --orka-bg-danger: #dc2626;           /* red-600 */
  --orka-bg-danger-soft: #fee2e2;      /* red-100 */
  --orka-bg-success: #059669;          /* emerald-600 */
  --orka-bg-success-soft: #d1fae5;     /* emerald-100 */
  --orka-bg-warning: #d97706;          /* amber-600 */
  --orka-bg-warning-soft: #fef3c7;     /* amber-100 */
  --orka-bg-info: #0284c7;             /* sky-600 */
  --orka-bg-info-soft: #dbeafe;        /* sky-100 */
  --orka-bg-intent: #7c3aed;           /* violet-600 */
  --orka-bg-intent-soft: #ede9fe;      /* violet-100 */

  /* === PRIORITY (§6.5) — 4 shades per key: color · soft · text · border. */
  --orka-priority-none: #94a3b8;         --orka-priority-none-soft: rgba(148,163,184,0.14);   --orka-priority-none-text: #64748b;       --orka-priority-none-border: rgba(148,163,184,0.40);
  --orka-priority-emergency: #dc2626;    --orka-priority-emergency-soft: rgba(220,38,38,0.14);--orka-priority-emergency-text: #991b1b;  --orka-priority-emergency-border: rgba(220,38,38,0.45);
  --orka-priority-fixed: #2563eb;        --orka-priority-fixed-soft: rgba(37,99,235,0.14);    --orka-priority-fixed-text: #1d4ed8;      --orka-priority-fixed-border: rgba(37,99,235,0.45);
  --orka-priority-high: #ea580c;         --orka-priority-high-soft: rgba(234,88,12,0.14);     --orka-priority-high-text: #c2410c;       --orka-priority-high-border: rgba(234,88,12,0.45);
  --orka-priority-medium: #ca8a04;       --orka-priority-medium-soft: rgba(202,138,4,0.14);   --orka-priority-medium-text: #a16207;     --orka-priority-medium-border: rgba(202,138,4,0.45);
  --orka-priority-low: #0891b2;          --orka-priority-low-soft: rgba(8,145,178,0.14);      --orka-priority-low-text: #0e7490;        --orka-priority-low-border: rgba(8,145,178,0.45);
  --orka-priority-later: #64748b;        --orka-priority-later-soft: rgba(100,116,139,0.14);  --orka-priority-later-text: #475569;      --orka-priority-later-border: rgba(100,116,139,0.45);
  --orka-priority-someday: #94a3b8;      --orka-priority-someday-soft: rgba(148,163,184,0.14);--orka-priority-someday-text: #64748b;    --orka-priority-someday-border: rgba(148,163,184,0.40);

  /* === TASK STATE (§6.4) — 4 shades per key: color · soft · text · border. */
  --orka-state-open: #64748b;            --orka-state-open-soft: rgba(100,116,139,0.14);      --orka-state-open-text: #475569;          --orka-state-open-border: rgba(100,116,139,0.45);
  --orka-state-in-progress: #2563eb;     --orka-state-in-progress-soft: rgba(37,99,235,0.14); --orka-state-in-progress-text: #1d4ed8;   --orka-state-in-progress-border: rgba(37,99,235,0.45);
  --orka-state-partial: #d97706;         --orka-state-partial-soft: rgba(217,119,6,0.14);     --orka-state-partial-text: #b45309;       --orka-state-partial-border: rgba(217,119,6,0.45);
  --orka-state-blocked: #dc2626;         --orka-state-blocked-soft: rgba(220,38,38,0.14);     --orka-state-blocked-text: #991b1b;       --orka-state-blocked-border: rgba(220,38,38,0.45);
  --orka-state-done: #059669;            --orka-state-done-soft: rgba(5,150,105,0.14);        --orka-state-done-text: #047857;          --orka-state-done-border: rgba(5,150,105,0.45);
  --orka-state-archived: #64748b;        --orka-state-archived-soft: rgba(100,116,139,0.12);  --orka-state-archived-text: #64748b;      --orka-state-archived-border: rgba(100,116,139,0.40);
  --orka-state-cancelled: #64748b;       --orka-state-cancelled-soft: rgba(100,116,139,0.12); --orka-state-cancelled-text: #64748b;     --orka-state-cancelled-border: rgba(100,116,139,0.40);
  --orka-state-deleted: #475569;         --orka-state-deleted-soft: rgba(71,85,105,0.12);     --orka-state-deleted-text: #334155;       --orka-state-deleted-border: rgba(71,85,105,0.40);
}

/* DARK MODE — class-based, applied on <html class="dark"> */
.dark {
  --orka-surface-page: #020617;
  --orka-surface-base: #0f172a;
  --orka-surface-raised: #1e293b;
  --orka-surface-subtle: #1e293b;
  --orka-surface-overlay: rgba(0,0,0,0.6);
  --orka-surface-glass: rgba(15,23,42,0.65);
  --orka-surface-selected: rgba(167,139,250,0.16);
  --orka-surface-dragging: rgba(167,139,250,0.20);
  --orka-surface-drop-target: rgba(74,222,128,0.16);
  --orka-surface-disabled: #1e293b;

  --orka-text-primary: #f1f5f9;
  --orka-text-secondary: #cbd5e1;
  --orka-text-tertiary: #64748b;
  --orka-text-disabled: #334155;
  --orka-text-inverse: #0f172a;
  --orka-text-success: #34d399;
  --orka-text-warning: #fbbf24;
  --orka-text-danger: #f87171;
  --orka-text-info: #38bdf8;
  --orka-text-intent: #a78bfa;

  --orka-border-subtle: #1e293b;
  --orka-border-default: #334155;
  --orka-border-strong: #64748b;
  --orka-border-focus: #a78bfa;

  --orka-shadow-sm: 0 1px 2px rgba(0,0,0,0.4);
  --orka-shadow-md: 0 4px 12px rgba(0,0,0,0.5);
  --orka-shadow-lg: 0 12px 32px rgba(0,0,0,0.6);
  --orka-shadow-glow-focus: 0 0 0 3px rgba(167,139,250,0.25);

  --orka-bg-danger: #b91c1c;           --orka-bg-danger-soft: rgba(248,113,113,0.15);
  --orka-bg-success: #047857;          --orka-bg-success-soft: rgba(52,211,153,0.15);
  --orka-bg-warning: #b45309;          --orka-bg-warning-soft: rgba(251,191,36,0.15);
  --orka-bg-info: #0369a1;             --orka-bg-info-soft: rgba(56,189,248,0.15);
  --orka-bg-intent: #6d28d9;           --orka-bg-intent-soft: rgba(167,139,250,0.18);

  /* === PRIORITY — dark mode (lighter shades for contrast). */
  --orka-priority-none: #94a3b8;        --orka-priority-none-soft: rgba(148,163,184,0.18);   --orka-priority-none-text: #cbd5e1;       --orka-priority-none-border: rgba(148,163,184,0.50);
  --orka-priority-emergency: #f87171;   --orka-priority-emergency-soft: rgba(248,113,113,0.18);--orka-priority-emergency-text: #fca5a5;--orka-priority-emergency-border: rgba(248,113,113,0.55);
  --orka-priority-fixed: #60a5fa;       --orka-priority-fixed-soft: rgba(96,165,250,0.18);   --orka-priority-fixed-text: #93c5fd;      --orka-priority-fixed-border: rgba(96,165,250,0.55);
  --orka-priority-high: #fb923c;        --orka-priority-high-soft: rgba(251,146,60,0.18);    --orka-priority-high-text: #fdba74;       --orka-priority-high-border: rgba(251,146,60,0.55);
  --orka-priority-medium: #facc15;      --orka-priority-medium-soft: rgba(250,204,21,0.18);  --orka-priority-medium-text: #fde047;     --orka-priority-medium-border: rgba(250,204,21,0.55);
  --orka-priority-low: #22d3ee;         --orka-priority-low-soft: rgba(34,211,238,0.18);     --orka-priority-low-text: #67e8f9;        --orka-priority-low-border: rgba(34,211,238,0.55);
  --orka-priority-later: #94a3b8;       --orka-priority-later-soft: rgba(148,163,184,0.18);  --orka-priority-later-text: #cbd5e1;      --orka-priority-later-border: rgba(148,163,184,0.50);
  --orka-priority-someday: #94a3b8;     --orka-priority-someday-soft: rgba(148,163,184,0.18);--orka-priority-someday-text: #cbd5e1;    --orka-priority-someday-border: rgba(148,163,184,0.50);

  /* === TASK STATE — dark mode. */
  --orka-state-open: #94a3b8;           --orka-state-open-soft: rgba(148,163,184,0.18);      --orka-state-open-text: #cbd5e1;          --orka-state-open-border: rgba(148,163,184,0.50);
  --orka-state-in-progress: #60a5fa;    --orka-state-in-progress-soft: rgba(96,165,250,0.18);--orka-state-in-progress-text: #93c5fd;   --orka-state-in-progress-border: rgba(96,165,250,0.55);
  --orka-state-partial: #fbbf24;        --orka-state-partial-soft: rgba(251,191,36,0.18);    --orka-state-partial-text: #fcd34d;       --orka-state-partial-border: rgba(251,191,36,0.55);
  --orka-state-blocked: #f87171;        --orka-state-blocked-soft: rgba(248,113,113,0.18);   --orka-state-blocked-text: #fca5a5;       --orka-state-blocked-border: rgba(248,113,113,0.55);
  --orka-state-done: #34d399;           --orka-state-done-soft: rgba(52,211,153,0.18);       --orka-state-done-text: #6ee7b7;          --orka-state-done-border: rgba(52,211,153,0.55);
  --orka-state-archived: #64748b;       --orka-state-archived-soft: rgba(100,116,139,0.16);  --orka-state-archived-text: #94a3b8;      --orka-state-archived-border: rgba(100,116,139,0.45);
  --orka-state-cancelled: #64748b;      --orka-state-cancelled-soft: rgba(100,116,139,0.16); --orka-state-cancelled-text: #94a3b8;     --orka-state-cancelled-border: rgba(100,116,139,0.45);
  --orka-state-deleted: #475569;        --orka-state-deleted-soft: rgba(71,85,105,0.16);     --orka-state-deleted-text: #94a3b8;       --orka-state-deleted-border: rgba(71,85,105,0.45);
}`;

const RAW_PRIORITY = `// /app/frontend/src/design-system/priority.js
// Colours flow via CSS variables (design-system/tokens.css). Four shades per
// key: color · soft · text · border. NEVER hardcode priority hex elsewhere.
export const PRIORITY_META = Object.freeze({
  none:      { key: 'none',      label: 'No priority',   short: 'None', color: 'var(--orka-priority-none)',      soft: 'var(--orka-priority-none-soft)',      text: 'var(--orka-priority-none-text)',      border: 'var(--orka-priority-none-border)' },
  emergency: { key: 'emergency', label: 'P-1 Emergency', short: 'P-1',  color: 'var(--orka-priority-emergency)', soft: 'var(--orka-priority-emergency-soft)', text: 'var(--orka-priority-emergency-text)', border: 'var(--orka-priority-emergency-border)' },
  fixed:     { key: 'fixed',     label: 'P0 Fixed',      short: 'P0',   color: 'var(--orka-priority-fixed)',     soft: 'var(--orka-priority-fixed-soft)',     text: 'var(--orka-priority-fixed-text)',     border: 'var(--orka-priority-fixed-border)' },
  high:      { key: 'high',      label: 'P1 High',       short: 'P1',   color: 'var(--orka-priority-high)',      soft: 'var(--orka-priority-high-soft)',      text: 'var(--orka-priority-high-text)',      border: 'var(--orka-priority-high-border)' },
  medium:    { key: 'medium',    label: 'P2 Medium',     short: 'P2',   color: 'var(--orka-priority-medium)',    soft: 'var(--orka-priority-medium-soft)',    text: 'var(--orka-priority-medium-text)',    border: 'var(--orka-priority-medium-border)' },
  low:       { key: 'low',       label: 'P3 Low',        short: 'P3',   color: 'var(--orka-priority-low)',       soft: 'var(--orka-priority-low-soft)',       text: 'var(--orka-priority-low-text)',       border: 'var(--orka-priority-low-border)' },
  later:     { key: 'later',     label: 'P4 Later',      short: 'P4',   color: 'var(--orka-priority-later)',     soft: 'var(--orka-priority-later-soft)',     text: 'var(--orka-priority-later-text)',     border: 'var(--orka-priority-later-border)' },
  someday:   { key: 'someday',   label: 'P5 Someday',    short: 'P5',   color: 'var(--orka-priority-someday)',   soft: 'var(--orka-priority-someday-soft)',   text: 'var(--orka-priority-someday-text)',   border: 'var(--orka-priority-someday-border)' },
});`;

const RAW_STATES = `// /app/frontend/src/design-system/states.js
// Colours flow via CSS variables (design-system/tokens.css). Four shades per
// key: color · soft · text · border. NEVER hardcode state hex elsewhere.
export const TASK_STATE_META = Object.freeze({
  open:       { key: 'open',       label: 'To Do',       short: 'To Do',    tone: 'neutral', intent: 'capture', color: 'var(--orka-state-open)',         soft: 'var(--orka-state-open-soft)',         text: 'var(--orka-state-open-text)',         border: 'var(--orka-state-open-border)' },
  inProgress: { key: 'inProgress', label: 'In progress', short: 'Active',   tone: 'info',    intent: 'focus',   color: 'var(--orka-state-in-progress)',  soft: 'var(--orka-state-in-progress-soft)',  text: 'var(--orka-state-in-progress-text)',  border: 'var(--orka-state-in-progress-border)' },
  partial:    { key: 'partial',    label: 'Partial',     short: 'Partial',  tone: 'recover', intent: 'recover', color: 'var(--orka-state-partial)',      soft: 'var(--orka-state-partial-soft)',      text: 'var(--orka-state-partial-text)',      border: 'var(--orka-state-partial-border)' },
  blocked:    { key: 'blocked',    label: 'Blocked',     short: 'Blocked',  tone: 'danger',  intent: 'warn',    color: 'var(--orka-state-blocked)',      soft: 'var(--orka-state-blocked-soft)',      text: 'var(--orka-state-blocked-text)',      border: 'var(--orka-state-blocked-border)' },
  done:       { key: 'done',       label: 'Done',        short: 'Done',     tone: 'success', intent: 'commit',  color: 'var(--orka-state-done)',         soft: 'var(--orka-state-done-soft)',         text: 'var(--orka-state-done-text)',         border: 'var(--orka-state-done-border)' },
  archived:   { key: 'archived',   label: 'Archived',    short: 'Archived', tone: 'muted',   intent: 'system',  color: 'var(--orka-state-archived)',     soft: 'var(--orka-state-archived-soft)',     text: 'var(--orka-state-archived-text)',     border: 'var(--orka-state-archived-border)' },
  cancelled:  { key: 'cancelled',  label: 'Cancelled',   short: 'Cancelled',tone: 'muted',   intent: 'system',  color: 'var(--orka-state-cancelled)',    soft: 'var(--orka-state-cancelled-soft)',    text: 'var(--orka-state-cancelled-text)',    border: 'var(--orka-state-cancelled-border)' },
  deleted:    { key: 'deleted',    label: 'Deleted',     short: 'Deleted',  tone: 'muted',   intent: 'system',  color: 'var(--orka-state-deleted)',      soft: 'var(--orka-state-deleted-soft)',      text: 'var(--orka-state-deleted-text)',      border: 'var(--orka-state-deleted-border)' },
});`;

const RAW_INTENTS = `// /app/frontend/src/design-system/intents.js
export const INTENT_META = Object.freeze({
  capture:   { key: 'capture',   label: 'Capture',   color: '#64748b', soft: '#64748b22' },
  focus:     { key: 'focus',     label: 'Focus',     color: '#0ea5e9', soft: '#0ea5e922' },
  plan:      { key: 'plan',      label: 'Plan',      color: '#6366f1', soft: '#6366f122' },
  commit:    { key: 'commit',    label: 'Commit',    color: '#059669', soft: '#05966922' },
  recover:   { key: 'recover',   label: 'Recover',   color: '#d97706', soft: '#d9770622' },
  warn:      { key: 'warn',      label: 'Warn',      color: '#dc2626', soft: '#dc262622' },
  reflect:   { key: 'reflect',   label: 'Reflect',   color: '#8b5cf6', soft: '#8b5cf622' },
  delegate:  { key: 'delegate',  label: 'Delegate',  color: '#0f766e', soft: '#0f766e22' },
  automate:  { key: 'automate',  label: 'Automate',  color: '#2563eb', soft: '#2563eb22' },
  private:   { key: 'private',   label: 'Private',   color: '#7c3aed', soft: '#7c3aed22' },
  system:    { key: 'system',    label: 'System',    color: '#2563eb', soft: '#2563eb22' },
});`;

const RAW_AGENT_TONES = `// /app/frontend/src/design-system/agentTones.js
export const AGENT_TONES = Object.freeze({
  thinking: { key: 'thinking', label: 'Thinking',  color: '#7c3aed', soft: '#7c3aed22', border: '#7c3aed77' },
  running:  { key: 'running',  label: 'Running',   color: '#0284c7', soft: '#0284c722', border: '#0284c777' },
  awaiting: { key: 'awaiting', label: 'Needs you', color: '#d97706', soft: '#d9770622', border: '#d9770677' },
  done:     { key: 'done',     label: 'Done',      color: '#059669', soft: '#05966922', border: '#05966977' },
  failure:  { key: 'failure',  label: 'Failed',    color: '#dc2626', soft: '#dc262622', border: '#dc262677' },
});`;

const RAW_PROJECTS = null; /* removed — work-area colours are user-defined, not part of the design system */

const RAW_EFFORT = `// /app/frontend/src/design-system/effortScale.js
export const DEFAULT_EFFORT_SCALE = Object.freeze([
  { key: 'empty',      label: 'Empty',      min: 0,  max: 0,   color: 'transparent', meaning: 'nothing planned' },
  { key: 'light',      label: 'Light',      min: 1,  max: 15,  color: '#3B82F6',     meaning: 'light day' },
  { key: 'balanced',   label: 'Balanced',   min: 16, max: 25,  color: '#22C55E',     meaning: 'balanced day' },
  { key: 'heavy',      label: 'Heavy',      min: 26, max: 35,  color: '#F59E0B',     meaning: 'heavy day' },
  { key: 'overloaded', label: 'Overloaded', min: 36, max: 45,  color: '#EF4444',     meaning: 'overloaded' },
  { key: 'doom',       label: 'Doom',       min: 46, max: 999, color: '#EF4444',     meaning: 'strongly discourage' },
]);`;

/* ———————————————————————— SHARED ATOMS ——————————————————————————————— */

/**
 * Swatch — a single color chip rendered via either a raw hex value (for JS
 * color metas like PRIORITY_META) or a live CSS variable (tokens.css).
 */
function Swatch({ label, code, subLabel, color, cssVar, soft, border }) {
  const bg = cssVar ? `var(${cssVar})` : color;
  const softBg = soft;
  return (
    <div
      className="rounded-lg border orka-border-subtle orka-surface-raised overflow-hidden"
      data-testid={`color-swatch-${String(label).toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
    >
      <div className="flex items-stretch h-14">
        <div
          className="flex-1 relative"
          style={{
            background: bg,
            backgroundColor: bg,
            backgroundImage: bg === 'transparent'
              ? 'repeating-conic-gradient(#e5e7eb 0% 25%, #ffffff 0% 50%) 50% / 12px 12px'
              : undefined,
            border: border ? `1px solid ${border}` : 'none',
          }}
          aria-label={`${label} swatch`}
        />
        {softBg && (
          <div
            className="w-10 border-l orka-border-subtle"
            style={{ background: softBg, backgroundColor: softBg }}
            aria-label={`${label} soft swatch`}
            title="soft"
          />
        )}
      </div>
      <div className="px-3 py-2">
        <OrkaText size="small" variant="primary" className="font-semibold block truncate">{label}</OrkaText>
        {subLabel && (
          <OrkaText size="micro" variant="tertiary" className="block truncate normal-case tracking-normal">{subLabel}</OrkaText>
        )}
        {code && (
          <code className="block mt-1 font-mono text-[10.5px] orka-text-secondary truncate">{code}</code>
        )}
      </div>
    </div>
  );
}

/** Copy-button wired to clipboard, with visual confirmation. */
function CopyButton({ text, testId }) {
  const [copied, setCopied] = useState(false);
  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1400);
    } catch { /* ignore */ }
  };
  return (
    <button
      type="button"
      onClick={onCopy}
      data-testid={testId}
      className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md border orka-border-subtle orka-surface-raised hover:orka-surface-selected orka-transition-colors text-[11px] font-medium orka-text-secondary"
    >
      {copied ? <Check className="w-3 h-3 orka-text-success" /> : <Copy className="w-3 h-3" />}
      {copied ? 'Copied' : 'Copy'}
    </button>
  );
}

/**
 * RawCodeExpander — <details>/<summary> expander showing the verbatim source
 * snippet, with a copy button so other agents can one-click grab the code.
 */
function RawCodeExpander({ sourcePath, code, testId }) {
  return (
    <details
      className="mt-4 rounded-lg border orka-border-subtle orka-surface-subtle"
      data-testid={testId}
    >
      <summary className="flex items-center justify-between gap-3 px-3 py-2 cursor-pointer select-none list-none [&::-webkit-details-marker]:hidden">
        <div className="flex items-center gap-2 min-w-0">
          <OrkaText size="micro" variant="tertiary" className="font-bold uppercase tracking-wider shrink-0">Raw code</OrkaText>
          <code className="font-mono text-[11px] orka-text-tertiary truncate">{sourcePath}</code>
        </div>
        <CopyButton text={code} testId={`${testId}-copy`} />
      </summary>
      <pre className="px-3 py-3 text-[11px] leading-relaxed orka-text-primary overflow-x-auto font-mono border-t orka-border-subtle orka-surface-base m-0">
        <code>{code}</code>
      </pre>
    </details>
  );
}

/** Small section header inside the showcase. */
function SubHeader({ eyebrow, title, description }) {
  return (
    <header className="mb-3">
      <OrkaText size="micro" variant="info" className="block font-bold tracking-wider uppercase">{eyebrow}</OrkaText>
      <OrkaText as="div" size="small" variant="primary" className="font-semibold mt-0.5">{title}</OrkaText>
      {description && (
        <OrkaText size="micro" variant="tertiary" className="block mt-1 normal-case tracking-normal">{description}</OrkaText>
      )}
    </header>
  );
}

/* ———————————————————————— SUB-SECTIONS ———————————————————————————————— */

const SURFACE_VARS = [
  { name: '--orka-surface-page',       light: '#fafaf9',            dark: '#020617' },
  { name: '--orka-surface-base',       light: '#ffffff',            dark: '#0f172a' },
  { name: '--orka-surface-raised',     light: '#ffffff',            dark: '#1e293b' },
  { name: '--orka-surface-subtle',     light: '#f5f5f4',            dark: '#1e293b' },
  { name: '--orka-surface-overlay',    light: 'rgba(0,0,0,0.4)',    dark: 'rgba(0,0,0,0.6)' },
  { name: '--orka-surface-glass',      light: 'rgba(255,255,255,0.65)', dark: 'rgba(15,23,42,0.65)' },
  { name: '--orka-surface-selected',   light: 'rgba(124,58,237,0.08)',  dark: 'rgba(167,139,250,0.16)' },
  { name: '--orka-surface-dragging',   light: 'rgba(124,58,237,0.12)',  dark: 'rgba(167,139,250,0.20)' },
  { name: '--orka-surface-drop-target',light: 'rgba(34,197,94,0.10)',   dark: 'rgba(74,222,128,0.16)' },
  { name: '--orka-surface-disabled',   light: '#e7e5e4',            dark: '#1e293b' },
];

function SurfaceSection() {
  return (
    <section data-testid="cs-surfaces">
      <SubHeader
        eyebrow="01 / Tokens"
        title="Surface tokens"
        description="Container fills. Live swatches render the active theme (toggle Dark mode in the header)."
      />
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
        {SURFACE_VARS.map((t) => (
          <Swatch
            key={t.name}
            label={t.name.replace('--orka-surface-', 'surface.')}
            subLabel={`light ${t.light} · dark ${t.dark}`}
            cssVar={t.name}
            code={`var(${t.name})`}
            border="var(--orka-border-subtle)"
          />
        ))}
      </div>
      <RawCodeExpander
        sourcePath="design-system/tokens.css"
        code={RAW_TOKENS_CSS}
        testId="cs-raw-tokens-css"
      />
    </section>
  );
}

const TEXT_VARS = [
  { name: '--orka-text-primary',   light: '#0f172a', dark: '#f1f5f9' },
  { name: '--orka-text-secondary', light: '#475569', dark: '#cbd5e1' },
  { name: '--orka-text-tertiary',  light: '#94a3b8', dark: '#64748b' },
  { name: '--orka-text-disabled',  light: '#cbd5e1', dark: '#334155' },
  { name: '--orka-text-inverse',   light: '#ffffff', dark: '#0f172a' },
  { name: '--orka-text-success',   light: '#059669', dark: '#34d399' },
  { name: '--orka-text-warning',   light: '#d97706', dark: '#fbbf24' },
  { name: '--orka-text-danger',    light: '#dc2626', dark: '#f87171' },
  { name: '--orka-text-info',      light: '#0284c7', dark: '#38bdf8' },
  { name: '--orka-text-intent',    light: '#7c3aed', dark: '#a78bfa' },
];

function TextSection() {
  return (
    <section data-testid="cs-text">
      <SubHeader
        eyebrow="02 / Tokens"
        title="Text tokens"
        description="Semantic text colours. Tailwind utilities: .orka-text-* or var(--orka-text-*)"
      />
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
        {TEXT_VARS.map((t) => (
          <Swatch
            key={t.name}
            label={t.name.replace('--orka-text-', 'text.')}
            subLabel={`light ${t.light} · dark ${t.dark}`}
            cssVar={t.name}
            code={`var(${t.name})`}
            border="var(--orka-border-subtle)"
          />
        ))}
      </div>
    </section>
  );
}

const BORDER_VARS = [
  { name: '--orka-border-subtle',  light: '#e7e5e4', dark: '#1e293b' },
  { name: '--orka-border-default', light: '#d6d3d1', dark: '#334155' },
  { name: '--orka-border-strong',  light: '#78716c', dark: '#64748b' },
  { name: '--orka-border-focus',   light: '#7c3aed', dark: '#a78bfa' },
];

function BorderSection() {
  return (
    <section data-testid="cs-borders">
      <SubHeader
        eyebrow="03 / Tokens"
        title="Border tokens"
        description="Border strokes. Focus ring uses border-focus."
      />
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {BORDER_VARS.map((t) => (
          <Swatch
            key={t.name}
            label={t.name.replace('--orka-border-', 'border.')}
            subLabel={`light ${t.light} · dark ${t.dark}`}
            cssVar={t.name}
            code={`var(${t.name})`}
          />
        ))}
      </div>
    </section>
  );
}

const ELEVATION_VARS = [
  { name: '--orka-shadow-sm',          light: '0 1px 2px rgba(15,23,42,0.06)',  dark: '0 1px 2px rgba(0,0,0,0.4)' },
  { name: '--orka-shadow-md',          light: '0 4px 12px rgba(15,23,42,0.08)', dark: '0 4px 12px rgba(0,0,0,0.5)' },
  { name: '--orka-shadow-lg',          light: '0 12px 32px rgba(15,23,42,0.12)', dark: '0 12px 32px rgba(0,0,0,0.6)' },
  { name: '--orka-shadow-glow-focus',  light: '0 0 0 3px rgba(124,58,237,0.20)', dark: '0 0 0 3px rgba(167,139,250,0.25)' },
];

function ElevationSection() {
  return (
    <section data-testid="cs-elevation">
      <SubHeader
        eyebrow="04 / Tokens"
        title="Elevation (shadows)"
        description="Drop-shadow depth ladder. Glow-focus is reserved for focus rings."
      />
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {ELEVATION_VARS.map((t) => (
          <div
            key={t.name}
            className="rounded-xl orka-surface-raised border orka-border-subtle p-4 flex flex-col items-start gap-3"
            style={{ boxShadow: `var(${t.name})` }}
            data-testid={`cs-elevation-${t.name.replace('--orka-shadow-', '')}`}
          >
            <div className="w-full h-10 rounded-md orka-surface-base" />
            <div>
              <OrkaText size="small" variant="primary" className="font-semibold block">
                {t.name.replace('--orka-shadow-', 'shadow.')}
              </OrkaText>
              <code className="block mt-0.5 font-mono text-[10px] orka-text-tertiary truncate max-w-[18ch]" title={t.light}>
                {t.light}
              </code>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

const SEMANTIC_BG_VARS = [
  { name: '--orka-bg-danger',       soft: '--orka-bg-danger-soft',  lightSolid: '#dc2626', lightSoft: '#fee2e2', darkSolid: '#b91c1c', darkSoft: 'rgba(248,113,113,0.15)' },
  { name: '--orka-bg-success',      soft: '--orka-bg-success-soft', lightSolid: '#059669', lightSoft: '#d1fae5', darkSolid: '#047857', darkSoft: 'rgba(52,211,153,0.15)' },
  { name: '--orka-bg-warning',      soft: '--orka-bg-warning-soft', lightSolid: '#d97706', lightSoft: '#fef3c7', darkSolid: '#b45309', darkSoft: 'rgba(251,191,36,0.15)' },
  { name: '--orka-bg-info',         soft: '--orka-bg-info-soft',    lightSolid: '#0284c7', lightSoft: '#dbeafe', darkSolid: '#0369a1', darkSoft: 'rgba(56,189,248,0.15)' },
  { name: '--orka-bg-intent',       soft: '--orka-bg-intent-soft',  lightSolid: '#7c3aed', lightSoft: '#ede9fe', darkSolid: '#6d28d9', darkSoft: 'rgba(167,139,250,0.18)' },
];

function SemanticBgSection() {
  return (
    <section data-testid="cs-semantic-bg">
      <SubHeader
        eyebrow="05 / Tokens"
        title="Semantic intent backgrounds"
        description="Solid and soft pairs. Use .orka-bg-<name> and .orka-bg-<name>-soft utilities."
      />
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3">
        {SEMANTIC_BG_VARS.map((t) => {
          const key = t.name.replace('--orka-bg-', '');
          return (
            <div key={t.name} className="rounded-lg border orka-border-subtle overflow-hidden">
              <div className="grid grid-cols-2 h-16">
                <div style={{ background: `var(${t.name})` }} aria-label={`${key} solid`} />
                <div style={{ background: `var(${t.soft})` }} aria-label={`${key} soft`} />
              </div>
              <div className="px-3 py-2 orka-surface-raised">
                <OrkaText size="small" variant="primary" className="font-semibold block">bg.{key}</OrkaText>
                <code className="block mt-0.5 font-mono text-[10px] orka-text-tertiary truncate">
                  solid {t.lightSolid} · soft {t.lightSoft}
                </code>
                <code className="block font-mono text-[10px] orka-text-tertiary truncate">
                  dark solid {t.darkSolid}
                </code>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}

/** Four-shade swatch row — one row per vocabulary key (color · soft · text · border). */
function FourShadeRow({ label, subLabel, color, soft, text, border, testId }) {
  return (
    <div
      className="rounded-lg border orka-border-subtle orka-surface-raised overflow-hidden"
      data-testid={testId}
    >
      <div className="grid grid-cols-4 h-12" role="img" aria-label={`${label} shades`}>
        <div style={{ background: color }} title="color" />
        <div style={{ background: soft }} title="soft" />
        <div
          className="flex items-center justify-center text-[13px] font-bold"
          style={{ background: soft, color: text }}
          title="text on soft"
        >Aa</div>
        <div
          className="orka-surface-raised"
          style={{ borderTop: 'none', borderRight: 'none', borderBottom: 'none', borderLeft: `4px solid ${border}` }}
          title="border"
        />
      </div>
      <div className="px-3 py-2">
        <OrkaText size="small" variant="primary" className="font-semibold block truncate">{label}</OrkaText>
        {subLabel && (
          <OrkaText size="micro" variant="tertiary" className="block truncate normal-case tracking-normal">{subLabel}</OrkaText>
        )}
      </div>
    </div>
  );
}

function PrioritySection() {
  return (
    <section data-testid="cs-priority">
      <SubHeader
        eyebrow="06 / Meta"
        title="Priority colors"
        description="PRIORITY_META — 4 shades per key (color · soft · text · border). Fully CSS-variable backed, dark-mode aware."
      />
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
        {Object.values(PRIORITY_META).map((p) => (
          <FourShadeRow
            key={p.key}
            testId={`cs-priority-${p.key}`}
            label={`${p.short} · ${p.label}`}
            subLabel={`key: ${p.key} · .orka-priority-${p.key}`}
            color={p.color}
            soft={p.soft}
            text={p.text}
            border={p.border}
          />
        ))}
      </div>
      <RawCodeExpander
        sourcePath="design-system/priority.js"
        code={RAW_PRIORITY}
        testId="cs-raw-priority"
      />
    </section>
  );
}

function StatesSection() {
  return (
    <section data-testid="cs-states">
      <SubHeader
        eyebrow="07 / Meta"
        title="Task state colors"
        description="TASK_STATE_META — 4 shades per key (color · soft · text · border). Fully CSS-variable backed, dark-mode aware."
      />
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
        {Object.values(TASK_STATE_META).map((s) => {
          const classPart = s.key === 'inProgress' ? 'in-progress' : s.key;
          return (
            <FourShadeRow
              key={s.key}
              testId={`cs-state-${s.key}`}
              label={s.label}
              subLabel={`key: ${s.key} · .orka-state-${classPart} · tone: ${s.tone}`}
              color={s.color}
              soft={s.soft}
              text={s.text}
              border={s.border}
            />
          );
        })}
      </div>
      <RawCodeExpander
        sourcePath="design-system/states.js"
        code={RAW_STATES}
        testId="cs-raw-states"
      />
    </section>
  );
}

function IntentsSection() {
  return (
    <section data-testid="cs-intents">
      <SubHeader
        eyebrow="08 / Meta"
        title="Intent vocabulary colors"
        description="INTENT_META — what a component helps the user DO (capture, focus, plan, commit...)."
      />
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {Object.values(INTENT_META).map((i) => (
          <Swatch
            key={i.key}
            label={i.label}
            subLabel={`key: ${i.key}`}
            color={i.color}
            soft={i.soft}
            code={i.color}
          />
        ))}
      </div>
      <RawCodeExpander
        sourcePath="design-system/intents.js"
        code={RAW_INTENTS}
        testId="cs-raw-intents"
      />
    </section>
  );
}

function AgentTonesSection() {
  return (
    <section data-testid="cs-agent-tones">
      <SubHeader
        eyebrow="09 / Meta"
        title="Agent tones"
        description="AGENT_TONES — the five-colour grammar used across ALL AI/agent surfaces."
      />
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {Object.values(AGENT_TONES).map((t) => (
          <div
            key={t.key}
            className="rounded-lg border overflow-hidden"
            style={{ borderColor: t.border, background: t.soft }}
            data-testid={`cs-tone-${t.key}`}
          >
            <div className="grid grid-cols-3 h-12">
              <div style={{ background: t.color }} aria-label="color" />
              <div style={{ background: t.soft }} aria-label="soft" />
              <div style={{ background: t.border }} aria-label="border" />
            </div>
            <div className="px-3 py-2 orka-surface-raised">
              <OrkaText size="small" variant="primary" className="font-semibold block">{t.label}</OrkaText>
              <code className="block mt-0.5 font-mono text-[10px] orka-text-tertiary truncate">
                {t.color} · {t.soft} · {t.border}
              </code>
            </div>
          </div>
        ))}
      </div>
      <RawCodeExpander
        sourcePath="design-system/agentTones.js"
        code={RAW_AGENT_TONES}
        testId="cs-raw-agent-tones"
      />
    </section>
  );
}

function ProjectsSection() { return null; /* removed — see header comment */ }

function EffortSection() {
  return (
    <section data-testid="cs-effort">
      <SubHeader
        eyebrow="10 / Meta"
        title="Effort scale colors"
        description="DEFAULT_EFFORT_SCALE — colours used by date-picker day cells and capacity rails."
      />
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        {DEFAULT_EFFORT_SCALE.map((b) => (
          <Swatch
            key={b.key}
            label={b.label}
            subLabel={`${b.min}–${b.max === 999 ? '∞' : b.max} · ${b.meaning}`}
            color={b.color}
            code={b.color}
            border="var(--orka-border-default)"
          />
        ))}
      </div>
      <RawCodeExpander
        sourcePath="design-system/effortScale.js"
        code={RAW_EFFORT}
        testId="cs-raw-effort"
      />
    </section>
  );
}

/* ———————————————————————————— PUBLIC COMPONENT ——————————————————————— */

/** Aggregate bundle — every raw snippet concatenated for one-click copy. */
const ALL_RAW_BUNDLE = [
  '/* =====================================================================',
  '   Orka Design System — Color tokens (aggregate export)',
  '   Includes: surface, text, border, elevation, semantic bg (CSS vars),',
  '             priority, state, intent, agent tones, effort (JS metas).',
  '   Excludes: work-area / project colours (user-defined per-org, not DS).',
  '   ===================================================================== */',
  '',
  RAW_TOKENS_CSS,
  '',
  RAW_PRIORITY,
  '',
  RAW_STATES,
  '',
  RAW_INTENTS,
  '',
  RAW_AGENT_TONES,
  '',
  RAW_EFFORT,
  '',
].join('\n');

/**
 * Top-level showcase — renders every color source as its own sub-section.
 * Mounted from DesignSystemPreviewPage via the `color-system` registry entry.
 */
export default function ColorSystemShowcase() {
  return (
    <div className="space-y-8" data-testid="color-system-showcase">
      <OrkaSurface variant="subtle" radius="lg" className="p-4 border orka-border-subtle">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <OrkaText size="small" variant="secondary" className="flex-1 min-w-[240px]">
            Every color currently registered in the Orka Design System — surface, text, border,
            elevation, semantic backgrounds, priority, state, intent, agent tones, effort.
            Each sub-section has a <strong>Raw code</strong> expander with the verbatim source,
            or grab everything in one go with the button on the right. <em>Work-area / project
            colours are intentionally excluded — those are user-defined during org setup.</em>
          </OrkaText>
          <CopyButton text={ALL_RAW_BUNDLE} testId="cs-copy-all" />
        </div>
        <details className="mt-3 rounded-lg border orka-border-subtle orka-surface-base" data-testid="cs-raw-all">
          <summary className="flex items-center justify-between gap-3 px-3 py-2 cursor-pointer select-none list-none [&::-webkit-details-marker]:hidden">
            <OrkaText size="micro" variant="tertiary" className="font-bold uppercase tracking-wider">
              Preview the full bundle
            </OrkaText>
            <OrkaText size="micro" variant="tertiary" className="normal-case tracking-normal">
              {ALL_RAW_BUNDLE.split('\n').length} lines
            </OrkaText>
          </summary>
          <pre className="px-3 py-3 text-[11px] leading-relaxed orka-text-primary overflow-x-auto font-mono border-t orka-border-subtle orka-surface-subtle m-0 max-h-[420px]">
            <code>{ALL_RAW_BUNDLE}</code>
          </pre>
        </details>
      </OrkaSurface>

      <SurfaceSection />
      <TextSection />
      <BorderSection />
      <ElevationSection />
      <SemanticBgSection />
      <PrioritySection />
      <StatesSection />
      <IntentsSection />
      <AgentTonesSection />
      <EffortSection />
    </div>
  );
}
